import { cn } from "@/lib/utils";

interface ProgressRingProps {
  percentage: number;
  size: "xs" | "sm" | "md" | "lg" | "xl";
  fillColor?: string;
  showLabel?: boolean;
  label?: string;
  trackFill?: string;
  /** Stroke of the unfinished arc. Defaults to the shared progress track. */
  trackColor?: string;
  labelColor?: string;
  className?: string;
}

const sizeConfig = {
  xs: { px: 16, stroke: 2, radius: 6.5, fontSize: 9 },
  sm: { px: 20, stroke: 2, radius: 9, fontSize: 11 },
  // 9px "100%" sits inside the stroke with a little air around it.
  md: { px: 42, stroke: 3, radius: 17, fontSize: 9 },
  lg: { px: 44, stroke: 3, radius: 16, fontSize: 10 },
  xl: { px: 60, stroke: 5, radius: 22, fontSize: 16 },
} as const;

export const ProgressRing = ({
  percentage,
  size,
  fillColor = "var(--progress)",
  showLabel,
  label,
  trackFill = "none",
  trackColor = "var(--progress-track)",
  labelColor = "var(--foreground)",
  className,
}: ProgressRingProps) => {
  const config = sizeConfig[size];
  const circumference = 2 * Math.PI * config.radius;
  const offset = circumference - (percentage / 100) * circumference;
  const center = config.px / 2;
  const displayLabel = showLabel ?? (label !== undefined || size === "lg");
  const labelText = label ?? `${Math.round(percentage)}%`;
  const isComplete = percentage >= 100;

  const renderProgressArc = () => {
    if (percentage <= 0) {
      return null;
    }

    if (isComplete) {
      return (
        <circle
          cx={center}
          cy={center}
          r={config.radius}
          fill="none"
          stroke={fillColor}
          strokeWidth={config.stroke}
        />
      );
    }

    return (
      <circle
        cx={center}
        cy={center}
        r={config.radius}
        fill="none"
        stroke={fillColor}
        strokeWidth={config.stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${center} ${center})`}
        className="transition-[stroke-dashoffset] duration-500 ease-out"
      />
    );
  };

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center",
        className
      )}
      style={{ width: config.px, height: config.px }}
    >
      <svg
        width={config.px}
        height={config.px}
        viewBox={`0 0 ${config.px} ${config.px}`}
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={config.radius}
          fill={trackFill}
          stroke={trackColor}
          strokeWidth={config.stroke}
        />
        {renderProgressArc()}
      </svg>
      {displayLabel && (
        <span
          className="absolute inset-0 flex items-center justify-center whitespace-nowrap font-bold leading-none pointer-events-none"
          style={{ fontSize: config.fontSize, color: labelColor }}
        >
          {labelText}
        </span>
      )}
    </span>
  );
};
